import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Glyph, TICK } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { PrimaryButton } from '~/components/ui';
import { prepareImage } from '~/lib/image';
import { logger } from '~/lib/logger';
import type { Photo } from '~/lib/photos';
import { PhotoCaptureScreen } from '~/screens/Inspection/PhotoCaptureScreen';
import { useTheme } from '~/theme/ThemeProvider';

/** A camera body, for the one action this screen offers. */
const CAMERA = 'M2 5.5h3l1.5-2h3l1.5 2h3v7H2zM6 9a2 2 0 1 0 4 0a2 2 0 1 0-4 0';

export interface BrandingScreenProps {
  onAdd: (photo: Photo) => void;
  onSkip: () => void;
  /** Back to the shoot step. The route has no system header to go back from. */
  onBack?: () => void;
}

/**
 * Optional step: the printed block in the corner of the pane.
 *
 * This is the input that identifies glass type and manufacturer — OEM versus
 * aftermarket. It has been skipped on almost every real job, so the feature is
 * effectively dark. It was called "logo silkscreen" until recently, which named the
 * printing PROCESS rather than the thing the operator has to find; hence the copy here
 * describes the physical object and the skip names what it costs.
 */
export function BrandingScreen({ onAdd, onSkip, onBack }: BrandingScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const [capturing, setCapturing] = useState(false);
  /** Which answer is selected. The frame opens with neither. */
  const [answer, setAnswer] = useState<'with' | 'without' | null>(null);

  if (capturing) {
    return (
      <PhotoCaptureScreen
        label="glass branding"
        hint="The printed block in a bottom corner — maker, standards marks, a code."
        onCancel={() => setCapturing(false)}
        onCapture={async (uri) => {
          setCapturing(false);
          try {
            const prepared = await prepareImage(uri);
            onAdd({
              id: `branding-${Date.now()}`,
              area: null,
              kind: 'branding',
              uri: prepared.uri,
              base64: prepared.base64,
              bytes: prepared.bytes,
              hasScaleRef: false,
            });
          } catch (e) {
            logger.error('[Branding] prepare failed', e);
          }
        }}
      />
    );
  }

  return (
    <Screen>
      <View style={{ paddingHorizontal: space.md }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pressable
            testID="branding-back"
            accessibilityRole="button"
            accessibilityLabel="Back"
            onPress={onBack}
            disabled={!onBack}
            style={{
              width: touch.min,
              height: touch.min,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>
              {onBack ? '‹' : ''}
            </Text>
          </Pressable>
          <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>Glass branding</Text>
        </View>
      </View>

      <View style={{ flex: 1, paddingHorizontal: space.md, gap: space.md }}>
        <View style={{ gap: space.sm }}>
          <Text style={[t.h2, { color: c.text }]}>Is there a maker's stamp?</Text>
          <Text style={[t.body, { color: c.dim, lineHeight: 22 }]}>
            The small print in a corner of the windscreen. It tells the assessor whether the glass
            is original.
          </Text>
        </View>

        {/*
          A question with two answers, which is what the frame draws — not an explanation
          with a capture button and a skip link. Both cards sit at the SAME weight: the
          "without it" one used to be drawn at 55 % opacity, the one signal direct sun
          removes entirely, and it read as unavailable rather than as the other answer.
          The ring and the mark carry the difference instead.
        */}
        <View accessibilityRole="radiogroup" style={{ flexDirection: 'row', gap: space.sm }}>
          {(
            [
              {
                key: 'with',
                title: 'With a stamp',
                detail: 'Logo, maker and codes etched in a corner.',
              },
              { key: 'without', title: 'No stamp', detail: 'Clear glass in every corner.' },
            ] as const
          ).map((o) => {
            const on = answer === o.key;
            return (
              <Pressable
                key={o.key}
                testID={`branding-${o.key}`}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                onPress={() => setAnswer(o.key)}
                style={{
                  flex: 1,
                  padding: space.sm,
                  gap: space.sm,
                  borderRadius: radius.lg,
                  backgroundColor: c.surface,
                  borderWidth: on ? 2 : 1,
                  borderColor: on ? c.brandPrimary : c.border,
                }}
              >
                <View
                  style={{
                    aspectRatio: 1,
                    borderRadius: radius.md,
                    backgroundColor: c.s3,
                  }}
                >
                  <View
                    style={{
                      position: 'absolute',
                      top: space.sm,
                      right: space.sm,
                      width: 24,
                      height: 24,
                      borderRadius: radius.pill,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: on ? c.brandPrimary : c.surface,
                      borderWidth: on ? 0 : 2,
                      borderColor: c.border2,
                    }}
                  >
                    {on ? <Glyph path={TICK} size={14} color={c.onPrimary} /> : null}
                  </View>
                </View>
                <View style={{ paddingHorizontal: space.xs, paddingBottom: space.xs }}>
                  <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>{o.title}</Text>
                  <Text style={[t.body, { color: c.dim, lineHeight: 20 }]}>{o.detail}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={{ paddingHorizontal: space.md, paddingBottom: space.md }}>
        <PrimaryButton
          testID={answer === 'without' ? 'skip-branding' : 'capture-branding'}
          label={answer === 'without' ? 'Continue without it' : 'Photograph the stamp'}
          glyph={answer === 'without' ? undefined : CAMERA}
          onPress={() => (answer === 'without' ? onSkip() : setCapturing(true))}
        />
      </View>
    </Screen>
  );
}
